import * as R from 'remeda';
import equal from 'fast-deep-equal/es6/react';
import { replace } from 'redux-first-history';
import { combineEpics } from 'redux-observable';
import { from } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  mergeMap,
  switchMap,
} from 'rxjs/operators';
import { endpoints } from '@rossum/api-client';
import { apiUrl } from '../../../constants/config';
import {
  granularities,
  GranularityT,
} from '../../../containers/Statistics/containers/Granularity';
import { getDummyData } from '../../../containers/Statistics/tempSeries';
import { errorHandler } from '../../../lib/api';
import { api } from '../../../lib/apiClient';
import { getThisMonthStart, getToday } from '../../../lib/dateUtils';
import { asArray, asScalar, parse, stringify } from '../../../lib/url';
import { setItem, STATISTICS_QUERY } from '../localStorage/actions';
import { canUseUsageReportsSelector } from '../organization/selectors';
import { enterStatistics } from '../ui/actions';
import { isActionOf, locationChange, makeEpic } from '../utils';
import {
  fetchStatistics,
  fetchStatisticsFulfilled,
  showTmpStatistics,
} from './actions';
import { validateDateQuery } from './helpers';

const onStatisticsQueryChangeEpic = makeEpic(action$ =>
  action$.pipe(
    filter(isActionOf(enterStatistics)),
    switchMap(() =>
      action$.pipe(
        filter(isActionOf(locationChange)),
        filter(
          ({
            payload: {
              location: { pathname },
            },
          }) => pathname.includes('/statistics')
        ),
        distinctUntilChanged(equal),
        mapTo(fetchStatistics())
      )
    )
  )
);

const fetchStatisticsEpic = makeEpic((action$, state$) =>
  action$.pipe(
    filter(isActionOf(fetchStatistics)),
    map(() => parse(state$.value.router.location.search)),
    filter(() => canUseUsageReportsSelector(state$.value) !== false),
    filter(({ users, queues, beginDate, endDate, groupBy }) =>
      [users, queues, beginDate, endDate, groupBy].every(R.identity())
    ),
    map(({ users, queues, beginDate, endDate, groupBy }) => ({
      filter: {
        users: asArray(users).map(id => `${apiUrl}/users/${id}`),
        queues: asArray(queues).map(id => `${apiUrl}/queues/${id}`),
        // Neither is null because of the previous filter.
        beginDate: asScalar(beginDate) ?? '',
        endDate: asScalar(endDate) ?? '',
      },
      groupBy: asArray(groupBy).filter(
        (g): g is string => typeof g === 'string'
      ),
    })),
    mergeMap(body =>
      from(api.request(endpoints.annotations.usageReport(body))).pipe(
        map(fetchStatisticsFulfilled),
        catchError(errorHandler)
      )
    )
  )
);

const showTmpStatisticsEpic = makeEpic((action$, state$) =>
  action$.pipe(
    filter(isActionOf(fetchStatistics)),
    map(() => parse(state$.value.router.location.search)),
    filter(({ beginDate, endDate, groupBy }) =>
      [beginDate, endDate, groupBy].every(R.identity())
    ),
    map(({ beginDate, endDate, groupBy }) =>
      showTmpStatistics({
        series: getDummyData({
          beginDate: asScalar(beginDate) ?? '',
          endDate: asScalar(endDate) ?? '',
          groupBy: asScalar(groupBy) ?? '',
        }),
        totals: {
          importedCount: 0,
          deletedCount: 0,
          exportedCount: 0,
          turnaroundAvgS: 0,
          correctionsPerDocumentAvg: 0,
          exportedOnTimeCount: 0,
          exportedLateCount: 0,
          exportedAutomatedCount: 0,
          timePerDocumentAvgS: 0,
          rejectedAutomaticallyCount: 0,
          rejectedManuallyCount: 0,
        },
      })
    )
  )
);

const storeStatisticsQueryEpic = makeEpic((action$, state$) =>
  action$.pipe(
    filter(isActionOf(fetchStatisticsFulfilled)),
    map(() => setItem(STATISTICS_QUERY, state$.value.router.location.search))
  )
);

const defaultStatisticsLocationEpic = makeEpic((action$, state$) =>
  action$.pipe(filter(isActionOf(enterStatistics))).pipe(
    map(action => action.payload),
    map(({ deletedUsersIdList, queuesIdList, usersIdList }) => {
      const { beginDate, endDate, queues, users, groupBy } = parse(
        state$.value.router.location.search
      );

      const {
        beginDate: cachedBeginDate,
        endDate: cachedEndDate,
        queues: cachedQueues,
        users: cachedUsers,
        groupBy: cachedGroupBy,
      } = parse(localStorage.getItem(STATISTICS_QUERY) || '');

      const _groupBy = groupBy || cachedGroupBy;
      const validGroupBy =
        typeof _groupBy === 'string' &&
        granularities.includes(_groupBy as GranularityT) &&
        _groupBy;

      const usersValue: (string | number)[] = users
        ? asArray(users)
        : cachedUsers
          ? asArray(cachedUsers)
          : usersIdList;

      const allUsers = [...usersIdList, ...deletedUsersIdList];
      const validUsers = usersValue.filter(userId =>
        allUsers.includes(Number(userId))
      );

      const queuesValue: (string | number)[] = queues
        ? asArray(queues)
        : cachedQueues
          ? asArray(cachedQueues)
          : queuesIdList[0] !== undefined
            ? [queuesIdList[0]]
            : [];

      const validQueues = queuesValue.filter(queueId =>
        queuesIdList.includes(Number(queueId))
      );

      const query = {
        ...validateDateQuery(
          beginDate ?? cachedBeginDate ?? null,
          endDate ?? cachedEndDate ?? null,
          {
            defaultBeginDate: getThisMonthStart(),
            defaultEndDate: getToday(),
          }
        ),
        groupBy: validGroupBy || 'week',
        users: validUsers,
        queues: validQueues,
      };

      return stringify(query);
    }),
    map(search => replace({ search }))
  )
);

export default combineEpics(
  onStatisticsQueryChangeEpic,
  fetchStatisticsEpic,
  defaultStatisticsLocationEpic,
  storeStatisticsQueryEpic,
  showTmpStatisticsEpic
);
